// Ultimate Card Game (UCG) - Post-Process Screen Effects
//
// Extracted from js/engine/renderer.js. Mixed onto Renderer.prototype (see
// the Object.assign() call at the bottom of renderer.js) rather than kept
// as free functions - every method here reads instance state (this.*),
// caches baked canvases on the instance (this._bloomCanvas etc.), or calls
// a sibling method, exactly like every other Renderer method, so this is a
// straight cut, not a rewrite.
//
// updateCriticalBaseAmbience() lives here alongside renderCriticalBasePulse()
// even though it spawns particle effects rather than drawing directly -
// both are the same "base is critically low on HP" feedback, just an
// ambience trigger and its on-screen pulse.
let _waterDropLUT = null;
function getWaterDropLUT() {
  if (_waterDropLUT) return _waterDropLUT;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const hctx = c.getContext('2d');
  const g = hctx.createRadialGradient(32 - 32 * 0.3, 32 - 32 * 0.3, 32 * 0.1, 32, 32, 32);
  g.addColorStop(0, 'rgba(240, 249, 255, 0.75)');
  g.addColorStop(0.5, 'rgba(186, 230, 253, 0.48)');
  g.addColorStop(0.85, 'rgba(14, 116, 144, 0.42)');
  g.addColorStop(1, 'rgba(2, 6, 23, 0.50)');
  hctx.fillStyle = g;
  hctx.beginPath();
  hctx.arc(32, 32, 32, 0, Math.PI * 2);
  hctx.fill();
  _waterDropLUT = c;
  return _waterDropLUT;
}

let _trailSmearLUT = null;
function getTrailSmearLUT() {
  if (_trailSmearLUT) return _trailSmearLUT;
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = 1;
  c.height = 64;
  const hctx = c.getContext('2d');
  const g = hctx.createLinearGradient(0, 0, 0, 64);
  g.addColorStop(0, 'rgba(224, 242, 254, 0)');
  g.addColorStop(0.65, 'rgba(186, 230, 253, 0.28)');
  g.addColorStop(1, 'rgba(255, 255, 255, 0.55)');
  hctx.fillStyle = g;
  hctx.fillRect(0, 0, 1, 64);
  _trailSmearLUT = c;
  return _trailSmearLUT;
}

export const postProcessMixin = {
  // Throttled state update (not a draw call)
  updateCriticalBaseAmbience(battlefield, particles) {
    const t = performance.now() / 1000;
    if (!this._lastCriticalPuff) this._lastCriticalPuff = { western: 0, eastern: 0 };
    for (const team of ['western', 'eastern']) {
      const base = battlefield.bases[team];
      if (!base || base.hp <= 0) continue;
      const ratio = base.hp / base.maxHp;
      if (ratio > 0.09) continue;
      if (t - this._lastCriticalPuff[team] < 0.60) continue;
      this._lastCriticalPuff[team] = t;
      particles.addLingeringSmokeColumn(base.x, base.y - 10, 2.6, 1.3);
      particles.addLight(base.x, base.y - 15, 130, '#ef4444', 0.85);
      particles.addShortCircuitSpark(base.x + (Math.random() * 40 - 20), base.y - 10);
      if (Math.random() < 0.8) {
        particles.addExplosion(base.x + (Math.random() * 50 - 25), base.y - 15, 14, '#ef4444', 10);
      }
    }
  },

  renderPostProcess(ctx, battlefield) {
    // Debug URL Flag: ?nopost=1 skips post-processing entirely (v3 §3 P0)
    if (this.game?.debugFlags?.noPost) {
      if (battlefield) this.renderCriticalBasePulse(ctx, battlefield);
      return;
    }

    // Quality-Shedding: Skip heavy post-processing passes during high simulation catch-up bursts
    if (this.skipHeavyPostFX) {
      this.skipHeavyPostFX = false;
      if (battlefield) this.renderCriticalBasePulse(ctx, battlefield);
      return;
    }

    // 🎬 Rich Cinematic Contrast & Midtone Deepening Pass (Canvas 2D native)
    // Low quality skips heavy fullscreen blend-mode passes (multiply/overlay) for maximum fillrate
    if (this.quality !== 'low') {
      // Base edge vignette is baked directly into _staticGlassCanvas (rendered below by
      // renderObservationGlassCanopy on all non-low tiers), saving a full-screen radial
      // gradient fillRect pass every frame on mobile & medium tiers.
      this.renderColorGrade(ctx, true);
      // renderBloom() downsamples the full Retina backing store and
      // re-composites it every frame - real fillrate/thermal cost on mobile
      // GPUs. multiply/overlay already skip mobile entirely (see
      // renderColorGrade below), but bloom only checked `quality !== 'low'`,
      // so it still ran every frame at mobile's default 'medium' tier -
      // the actual mobile thermal driver named in
      // docs/PERFORMANCE_AND_ONLINE_STABILITY_PLAN_V2_2026-09.md §3.1.
      // Reserved for 'high'/'ultra' (an explicit player choice past the
      // default) so the common iPhone case skips it outright.
      if (!this.isMobile || this.quality === 'high' || this.quality === 'ultra') {
        this.renderBloom(ctx);
      }
      // Barrel blur is only rendered on desktop at ultra quality
      if (!this.isMobile && this.quality === 'ultra') {
        this.renderBarrelBlur(ctx);
      }
    }

    // 🪟 Observation Cockpit Glass Canopy (structure, sheen, sun glare)
    // This is the physical window of the command observation deck itself,
    // not part of the map's terrain - every map is viewed through the same
    // glass, so the canopy renders identically regardless of mapId. Replaces
    // Voidglass's former bespoke vignette/fringe (see renderVoidglassFrame -
    // it now only keeps the deep-space top/bottom atmosphere framing that's
    // actually specific to that map).
    this.renderObservationGlassCanopy(ctx, battlefield);

    // 🌧️ Earth rain trickling down that glass only makes sense while the
    // human map's own rain cycle is actually running (see weather.js) -
    // alien environments (Sul homeworld spore-wind, Swamp rainforest
    // mist/fog, Voidglass deep space, Stratos dreadnought) never have Earth rain falling in the
    // scene (renderer.js gates the actual battlefield rain to the human map
    // the same way), so droplets on the glass there would read as a stray
    // leftover effect.
    if (battlefield?.mapId !== 'sul' && battlefield?.mapId !== 'swamp' && battlefield?.mapId !== 'voidglass' && battlefield?.mapId !== 'stratos') {
      this.renderObservationGlassRain(ctx, battlefield);
    }

    // 🌌 Voidglass deep-space top/bottom atmosphere framing - see
    // backgroundVoidglass.js's renderVoidglassFrame for why this lives here
    // rather than in the world-space static/dynamic background contract.
    if (battlefield?.mapId === 'voidglass') {
      this.renderVoidglassFrame(ctx, battlefield);
    }

    // 🔭 Mobile Portrait 50% Wide-Overview Vignette & Subtle Edge-Strip Lens Distortion
    this.renderPortraitWideVignetteAndLens(ctx);

    // 🎬 CRT scanlines are hardware-accelerated via CSS (.battlefield-scanlines)
    // with 0 Canvas 2D CPU/GPU rasterization cost. Film grain overlay is reserved for ultra.
    if (this.quality === 'ultra') {
      this.renderFilmGrain(ctx);
    }

    if (battlefield) this.renderCriticalBasePulse(ctx, battlefield);
  },

  // Cheap downsample-and-add bloom: shrinking the frame to a fraction of its
  // size is a free box-blur, and re-compositing it with 'lighter' blending
  // makes bright saturated pixels (explosions, energy bars, neon accents)
  // visibly glow. Ultra tier uses 2-pass Dual-Kawase bloom for silky neon.
  renderBloom(ctx) {
    // Debug URL Flag: ?nobloom=1 skips bloom pass (v3 §3 P0)
    if (this.game?.debugFlags?.noBloom) return;

    const dw = this.canvas.width, dh = this.canvas.height;
    const isUltra = this.quality === 'ultra';
    const div = this.isMobile ? 8 : (isUltra ? 6 : 7);
    const targetW = Math.min(320, Math.max(1, Math.round(dw / div)));
    const targetH = Math.min(180, Math.max(1, Math.round(dh / div)));

    if (!this._bloomCanvas || this._bloomSrcW !== dw || this._bloomSrcH !== dh) {
      this._bloomCanvas = document.createElement('canvas');
      this._bloomSrcW = dw;
      this._bloomSrcH = dh;
      this._bloomCanvas.width = targetW;
      this._bloomCanvas.height = targetH;
      this._bloomCtx = this._bloomCanvas.getContext('2d');

      if (isUltra) {
        this._bloomCanvas2 = document.createElement('canvas');
        this._bloomCanvas2.width = Math.max(1, Math.round(targetW / 2));
        this._bloomCanvas2.height = Math.max(1, Math.round(targetH / 2));
        this._bloomCtx2 = this._bloomCanvas2.getContext('2d');
      }
    }
    const bw = this._bloomCanvas.width, bh = this._bloomCanvas.height;

    // Mobile High Tier: Throttle downsampling update to 30 Hz (every 2nd frame) while blitting at 60 Hz (v3 §3 P1c #2)
    let shouldUpdateBloom = true;
    if (this.isMobile && this.quality === 'high') {
      this._bloomSkipToggle = !this._bloomSkipToggle;
      if (this._bloomSkipToggle && this._bloomHasDrawn) {
        shouldUpdateBloom = false;
      }
    }

    if (shouldUpdateBloom) {
      this._bloomCtx.clearRect(0, 0, bw, bh);
      this._bloomCtx.drawImage(this.canvas, 0, 0, bw, bh);
      this._bloomHasDrawn = true;
    }

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = isUltra ? 0.25 : 0.35;
    ctx.drawImage(this._bloomCanvas, 0, 0, bw, bh, 0, 0, dw, dh);

    if (isUltra && this._bloomCanvas2) {
      const bw2 = this._bloomCanvas2.width, bh2 = this._bloomCanvas2.height;
      if (shouldUpdateBloom) {
        this._bloomCtx2.clearRect(0, 0, bw2, bh2);
        this._bloomCtx2.drawImage(this._bloomCanvas, 0, 0, bw2, bh2);
      }
      ctx.globalAlpha = 0.20;
      ctx.drawImage(this._bloomCanvas2, 0, 0, bw2, bh2, 0, 0, dw, dh);
    }
    ctx.restore();
  },

  // 🎬 Cinematic Color & Contrast Grade (100% GPU Canvas 2D native, zero latency overhead)
  // Re-creates the rich, deep shadows, punchy midtones and subtle sci-fi saturation natively
  renderColorGrade(ctx, skipBaseVignette = false) {
    const trackingProgress = this.viewport?.trackingProgress || 0;
    const hush = this.particles?.bioPlasmaHush;
    const hasHush = !!(hush && this.quality !== 'low');
    const hasColorGradeBlends = !this.isMobile && (this.quality === 'high' || this.quality === 'ultra');

    // Fast-path: When the static edge vignette is already baked into _staticGlassCanvas
    // and neither desktop multiply/overlay grading, unit focus tracking, nor Sul hush is active,
    // skip the entire full-screen pass (0 draw calls, 0 state changes).
    if (skipBaseVignette && !hasColorGradeBlends && trackingProgress <= 0.001 && !hasHush) {
      return;
    }

    const dw = this.canvas.width, dh = this.canvas.height;
    ctx.save();

    // 1. Midtone & Deep Shadow Anchoring (skipped on mobile and medium tier to conserve fillrate)
    if (hasColorGradeBlends) {
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = 'rgba(200, 205, 222, 1.0)';
      ctx.fillRect(0, 0, dw, dh);
    }

    // 2. Highlight Boost & S-Curve Dynamic Contrast (reserved for desktop ultra tier to conserve fillrate)
    if (!this.isMobile && this.quality === 'ultra') {
      ctx.globalCompositeOperation = 'overlay';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.32)';
      ctx.fillRect(0, 0, dw, dh);
    }

    // 3. Cinematic Contrast & Subtle Edge Vignette (Soft falloff to keep bases and edge turrets well-lit)
    ctx.globalCompositeOperation = 'source-over';
    const cx = dw / 2, cy = dh / 2;
    const maxR = Math.hypot(cx, cy);
    if (!skipBaseVignette) {
      if (!this._vignetteGrad || this._vignetteW !== dw || this._vignetteH !== dh) {
        this._vignetteW = dw;
        this._vignetteH = dh;
        const vignette = ctx.createRadialGradient(cx, cy, maxR * 0.60, cx, cy, maxR * 0.98);
        vignette.addColorStop(0, 'rgba(0, 0, 0, 0)');
        vignette.addColorStop(0.55, 'rgba(0, 0, 0, 0)');
        vignette.addColorStop(0.85, 'rgba(3, 6, 14, 0.08)');
        vignette.addColorStop(1, 'rgba(1, 2, 8, 0.18)');
        this._vignetteGrad = vignette;
      }
      ctx.fillStyle = this._vignetteGrad;
      ctx.fillRect(0, 0, dw, dh);
    }

    // 4. Desktop-Exclusive Unit Focus Vignette ("dabei verstärkt sich die vignettierung spürbar aber noch subtil")
    if (trackingProgress > 0.001) {
      if (!this._focusVignetteGrad || this._focusVignetteW !== dw || this._focusVignetteH !== dh) {
        this._focusVignetteW = dw;
        this._focusVignetteH = dh;
        const focusVig = ctx.createRadialGradient(cx, cy, maxR * 0.38, cx, cy, maxR * 0.96);
        focusVig.addColorStop(0, 'rgba(0, 0, 0, 0)');
        focusVig.addColorStop(0.40, 'rgba(0, 0, 0, 0)');
        focusVig.addColorStop(0.70, 'rgba(2, 5, 12, 0.14)');
        focusVig.addColorStop(1, 'rgba(1, 2, 8, 0.36)');
        this._focusVignetteGrad = focusVig;
      }
      ctx.save();
      ctx.globalAlpha = Math.min(1, trackingProgress * 0.9);
      ctx.fillStyle = this._focusVignetteGrad;
      ctx.fillRect(0, 0, dw, dh);
      ctx.restore();
    }

    // 5. Sul Bio-Plasma Hush Phase (docs/BIO_PLASMA_PLAN.md §7.6):
    // Quality-gated desaturation and tightening vignette during Phase 0 silence
    if (hasHush) {
      const prog = Math.max(0, Math.min(1.0, 1.0 - (hush.duration / hush.maxDuration)));
      const hushFactor = Math.sin(prog * Math.PI);

      ctx.save();
      // Desaturating color layer
      ctx.globalCompositeOperation = 'color';
      ctx.fillStyle = `rgba(120, 120, 130, ${0.75 * hushFactor})`;
      ctx.fillRect(0, 0, dw, dh);

      // Tightening dark vignette
      ctx.globalCompositeOperation = 'source-over';
      const hushVigGrad = ctx.createRadialGradient(cx, cy, maxR * (0.55 - prog * 0.25), cx, cy, maxR * 0.92);
      hushVigGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
      hushVigGrad.addColorStop(0.5, `rgba(10, 5, 20, ${0.35 * hushFactor})`);
      hushVigGrad.addColorStop(1, `rgba(2, 1, 8, ${0.75 * hushFactor})`);
      ctx.fillStyle = hushVigGrad;
      ctx.fillRect(0, 0, dw, dh);
      ctx.restore();
    }

    ctx.restore();
  },

  // Very soft edge-only blur - an old CRT/lens's corners going gently soft
  // while the center stays sharp, not a uniform blur over the whole frame
  // (that would just look like a dirty lens, and would fight readability).
  // Same downsample-is-a-free-box-blur trick as renderBloom, but the
  // upscaled result is masked to a radial ring first so only the rim ever
  // picks it up, then blended in at low alpha - "ganz leicht", meant to be
  // felt more than seen, stacking with the scanlines/grain/vignette below
  // for the overall old-TV impression.
  renderBarrelBlur(ctx) {
    const dw = this.canvas.width, dh = this.canvas.height;
    const targetW = Math.min(240, Math.max(1, Math.round(dw / 10)));
    const targetH = Math.min(135, Math.max(1, Math.round(dh / 10)));
    if (!this._barrelDownCanvas || this._barrelW !== dw || this._barrelH !== dh) {
      this._barrelW = dw;
      this._barrelH = dh;
      this._barrelDownCanvas = document.createElement('canvas');
      this._barrelDownCanvas.width = targetW;
      this._barrelDownCanvas.height = targetH;
      this._barrelDownCtx = this._barrelDownCanvas.getContext('2d');

      this._barrelLayerCanvas = document.createElement('canvas');
      this._barrelLayerCanvas.width = dw;
      this._barrelLayerCanvas.height = dh;
      this._barrelLayerCtx = this._barrelLayerCanvas.getContext('2d');

      // Only depends on the canvas's own aspect ratio, not frame content -
      // built once per resize alongside the buffers above.
      this._barrelMaskCanvas = document.createElement('canvas');
      this._barrelMaskCanvas.width = dw;
      this._barrelMaskCanvas.height = dh;
      const mctx = this._barrelMaskCanvas.getContext('2d');
      const cx = dw / 2, cy = dh / 2;
      const maxR = Math.hypot(cx, cy);
      const grad = mctx.createRadialGradient(cx, cy, maxR * 0.6, cx, cy, maxR);
      grad.addColorStop(0, 'rgba(0, 0, 0, 0)');
      grad.addColorStop(1, 'rgba(0, 0, 0, 1)');
      mctx.fillStyle = grad;
      mctx.fillRect(0, 0, dw, dh);
    }

    const dcw = this._barrelDownCanvas.width, dch = this._barrelDownCanvas.height;
    this._barrelDownCtx.clearRect(0, 0, dcw, dch);
    // Desktop-ultra Readback Consolidation (v3 §3 P1c #1):
    // Downsample from the already scaled _bloomCanvas instead of resolving full Retina framebuffer
    const src = this._bloomCanvas || this.canvas;
    this._barrelDownCtx.drawImage(src, 0, 0, dcw, dch);

    // Upscale back to full size into a scratch layer, then punch the
    // center out of THAT layer (destination-in) - never the main ctx
    // directly, which would erase the sharp scene under the transparent
    // part of the mask instead of just skipping it.
    const lctx = this._barrelLayerCtx;
    lctx.clearRect(0, 0, dw, dh);
    lctx.drawImage(this._barrelDownCanvas, 0, 0, dcw, dch, 0, 0, dw, dh);
    lctx.globalCompositeOperation = 'destination-in';
    lctx.drawImage(this._barrelMaskCanvas, 0, 0);
    lctx.globalCompositeOperation = 'source-over';

    ctx.save();
    ctx.globalAlpha = 0.45;
    ctx.drawImage(this._barrelLayerCanvas, 0, 0);
    ctx.restore();
  },

  // Static per-pixel noise tile, layered over the scanlines to break up flat
  // gradient banding and reinforce the CRT/arcade-cabinet feel. Baked once.
  renderFilmGrain(ctx) {
    if (!this._grainPattern) {
      const size = 64;
      const tile = document.createElement('canvas');
      tile.width = size;
      tile.height = size;
      const tctx = tile.getContext('2d');
      const imgData = tctx.createImageData(size, size);
      for (let i = 0; i < imgData.data.length; i += 4) {
        const v = Math.floor(Math.random() * 255);
        imgData.data[i] = v;
        imgData.data[i + 1] = v;
        imgData.data[i + 2] = v;
        imgData.data[i + 3] = Math.random() * 40;
      }
      tctx.putImageData(imgData, 0, 0);
      this._grainPattern = ctx.createPattern(tile, 'repeat');
    }
    ctx.save();
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = this._grainPattern;
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.restore();
  },

  // Screen-wide dread pulse while either base is critically low on HP - an
  // unmistakable red alert heartbeat beacon hugging the full perimeter so the
  // danger is felt immediately across the entire viewport.
  // 🚨 Critical Base Red Alert Vignette & Emergency Alarm Pulse (Death's door at <= 9% HP)
  renderCriticalBasePulse(ctx, battlefield) {
    const western = battlefield.bases?.western;
    const eastern = battlefield.bases?.eastern;
    const worstRatio = Math.min(
      western && western.hp > 0 ? western.hp / western.maxHp : 1,
      eastern && eastern.hp > 0 ? eastern.hp / eastern.maxHp : 1
    );
    if (worstRatio > 0.09) return;

    const t = performance.now() * 0.001;
    const closeness = 1.0 - (worstRatio / 0.09); // 0.0 at 9% threshold, 1.0 at 0 HP
    const pulse = 0.5 + 0.5 * Math.sin(t * 4.5); // Urgent 4.5 rad/s emergency beacon tempo
    // Vivid, unmissable red alert alpha (0.28 to 0.65)
    const alpha = (0.28 + closeness * 0.22) * (0.55 + 0.45 * pulse);

    const dw = this.canvas.width, dh = this.canvas.height;
    ctx.save();
    ctx.globalAlpha = alpha;

    // Aspect-ratio corrected elliptical perimeter vignette
    ctx.translate(dw / 2, dh / 2);
    ctx.scale(dw / dh, 1);
    const gradR = dh * 0.52;
    if (!this._criticalPulseGrad || this._critPulseH !== dh) {
      this._critPulseH = dh;
      const grad = ctx.createRadialGradient(0, 0, gradR * 0.35, 0, 0, gradR);
      grad.addColorStop(0, 'rgba(220, 38, 38, 0)');
      grad.addColorStop(0.50, 'rgba(220, 38, 38, 0.12)');
      grad.addColorStop(0.80, 'rgba(220, 38, 38, 0.55)');
      grad.addColorStop(1.0, 'rgba(185, 28, 28, 0.95)');
      this._criticalPulseGrad = grad;
    }
    ctx.fillStyle = this._criticalPulseGrad;
    ctx.fillRect(-gradR, -gradR, gradR * 2, gradR * 2);
    ctx.restore();
  },

  // Mobile Portrait ~50% Wide-Zoom Vignette & Performance-Sensitive Edge Lens Distortion:
  // When zoomed out to the 50% perspective in Mobile Portrait, the top and bottom
  // ~14% bands (outside the central combat lane) receive a subtle 5-strip optical
  // barrel/pincushion refraction warp plus a cached atmospheric lens vignette.
  // Zero offscreen canvas allocation and 0.0ms cost in Default Close zoom.
  renderPortraitWideVignetteAndLens(ctx) {
    const wideProgress = this.viewport?.getWideZoomProgress ? this.viewport.getWideZoomProgress() : 0;
    if (wideProgress <= 0.005) return;

    const dw = this.canvas.width;
    const dh = this.canvas.height;
    if (dw <= 0 || dh <= 0) return;

    const bandH = Math.max(16, Math.round(dh * 0.14));
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // 1. Subtle 5-strip horizontal lens curvature in the top & bottom vignette bands
    if (this.quality !== 'low' && this.canvas) {
      const SLICE_COUNT = 5;
      const sliceH = Math.max(2, Math.floor(bandH / SLICE_COUNT));
      // Iterate from outer rim (i = SLICE_COUNT - 1) inward (i = 0) so vertical shifts
      // sample from inner rows before those rows are modified.
      for (let i = SLICE_COUNT - 1; i >= 0; i--) {
        const rimFrac = (i + 1) / SLICE_COUNT; // 0.2 (inner) -> 1.0 (outer edge)
        const warp = rimFrac * rimFrac * 0.036 * wideProgress;
        const insetX = Math.round(dw * warp * 0.5);
        const srcW = Math.max(1, dw - insetX * 2);
        const shiftY = Math.round(rimFrac * 2.5 * wideProgress);

        // Top band slice (sy = 0 at very top edge when i = SLICE_COUNT - 1)
        const topDestY = (SLICE_COUNT - 1 - i) * sliceH;
        const topSrcY = Math.min(dh - sliceH, topDestY + shiftY);
        if (insetX > 0 || shiftY > 0) {
          ctx.drawImage(this.canvas, insetX, topSrcY, srcW, sliceH, 0, topDestY, dw, sliceH);
        }

        // Bottom band slice (botDestY = dh - sliceH at very bottom edge when i = SLICE_COUNT - 1)
        const botDestY = dh - (SLICE_COUNT - i) * sliceH;
        const botSrcY = Math.max(0, botDestY - shiftY);
        if (insetX > 0 || shiftY > 0) {
          ctx.drawImage(this.canvas, insetX, botSrcY, srcW, sliceH, 0, botDestY, dw, sliceH);
        }
      }
    }

    // 2. Cached top/bottom wide-angle lens vignette + subtle chromatic rim dispersion
    const vigH = Math.round(bandH * 1.35);
    if (!this._widePortraitTopVig || this._widePortraitVigDh !== dh || this._widePortraitVigH !== vigH) {
      this._widePortraitVigDh = dh;
      this._widePortraitVigH = vigH;

      const topGrad = ctx.createLinearGradient(0, 0, 0, vigH);
      topGrad.addColorStop(0, 'rgba(1, 3, 10, 0.58)');
      topGrad.addColorStop(0.22, 'rgba(12, 74, 110, 0.16)');
      topGrad.addColorStop(0.55, 'rgba(2, 6, 23, 0.10)');
      topGrad.addColorStop(1, 'rgba(2, 6, 23, 0)');
      this._widePortraitTopVig = topGrad;

      const botGrad = ctx.createLinearGradient(0, dh - vigH, 0, dh);
      botGrad.addColorStop(0, 'rgba(2, 6, 23, 0)');
      botGrad.addColorStop(0.45, 'rgba(2, 6, 23, 0.12)');
      botGrad.addColorStop(0.78, 'rgba(30, 58, 138, 0.16)');
      botGrad.addColorStop(1, 'rgba(1, 3, 10, 0.64)');
      this._widePortraitBotVig = botGrad;
    }

    ctx.globalAlpha = wideProgress;
    ctx.fillStyle = this._widePortraitTopVig;
    ctx.fillRect(0, 0, dw, vigH);

    ctx.fillStyle = this._widePortraitBotVig;
    ctx.fillRect(0, dh - vigH, dw, vigH);

    ctx.restore();
  },

  // Frees the static glass canopy canvas backing store.
  // Re-baked automatically on the next frame via ensureStaticGlassCanopy().
  releaseStaticGlassCanopy() {
    if (this._staticGlassCanvas) {
      this._staticGlassCanvas.width = 0;
      this._staticGlassCanvas.height = 0;
    }
    this._staticGlassW = 0;
    this._staticGlassH = 0;
    this._staticGlassDpr = 0;
  },

  // Frees all downsampled post-process canvases (bloom, barrel, glass downsample)
  // to shed backing memory when in menus or opening packs. Re-allocated on demand on next frame.
  releasePostProcessCaches() {
    this.releaseStaticGlassCanopy();
    const list = [
      this._bloomCanvas,
      this._bloomCanvas2,
      this._barrelDownCanvas,
      this._barrelLayerCanvas,
      this._barrelMaskCanvas,
      this._glassDownCanvas
    ];
    for (const c of list) {
      if (c) {
        c.width = 0;
        c.height = 0;
      }
    }
    this._bloomCanvas = null;
    this._bloomCanvas2 = null;
    this._bloomCtx = null;
    this._bloomCtx2 = null;
    this._bloomSrcW = 0;
    this._bloomSrcH = 0;
    this._barrelDownCanvas = null;
    this._barrelLayerCanvas = null;
    this._barrelMaskCanvas = null;
    this._glassDownCanvas = null;
    this._barrelW = 0;
    this._barrelH = 0;
  },

  // Bakes the static observation cockpit glass canopy (seams, specular highlights,
  // chromatic dispersion, border bevels, corner vignette) into an offscreen canvas.
  // Executed once on load or resize.
  ensureStaticGlassCanopy(width, height, dpr) {
    if (this._staticGlassCanvas && this._staticGlassW === width && this._staticGlassH === height && this._staticGlassDpr === dpr) {
      return;
    }
    this._staticGlassW = width;
    this._staticGlassH = height;
    this._staticGlassDpr = dpr;

    if (!this._staticGlassCanvas) {
      this._staticGlassCanvas = document.createElement('canvas');
    }
    if (this._staticGlassCanvas.width !== width || this._staticGlassCanvas.height !== height) {
      this._staticGlassCanvas.width = width;
      this._staticGlassCanvas.height = height;
    }
    const gctx = this._staticGlassCanvas.getContext('2d');
    gctx.setTransform(1, 0, 0, 1, 0, 0);
    gctx.clearRect(0, 0, width, height);

    const seamOffset = Math.round(32 * dpr);
    const curveRadius = Math.round(24 * dpr);
    const extY = Math.round(100 * dpr);
    const yStart = -extY;
    const yEnd = height + extY;

    const seams = [
      {
        xStart: Math.round((-extY / height) * seamOffset),
        xEnd: Math.round(seamOffset + (extY / height) * seamOffset),
        isL: true
      },
      {
        xStart: Math.round(width - (-extY / height) * seamOffset),
        xEnd: Math.round(width - seamOffset - (extY / height) * seamOffset),
        isL: false
      }
    ];

    for (const seam of seams) {
      const { xStart, xEnd, isL } = seam;
      const cpX = (xStart + xEnd) * 0.5 + (isL ? -2 * dpr : 2 * dpr);
      const cpY = height * 0.5;

      gctx.save();
      // 1. Wide outer ambient glass roll-off
      gctx.strokeStyle = 'rgba(224, 242, 254, 0.018)';
      gctx.lineWidth = curveRadius * 2.4;
      gctx.beginPath();
      gctx.moveTo(xStart, yStart);
      gctx.quadraticCurveTo(cpX, cpY, xEnd, yEnd);
      gctx.stroke();

      // 2. Wide cylindrical specular body
      gctx.strokeStyle = 'rgba(255, 255, 255, 0.035)';
      gctx.lineWidth = curveRadius * 1.4;
      gctx.beginPath();
      gctx.moveTo(xStart, yStart);
      gctx.quadraticCurveTo(cpX, cpY, xEnd, yEnd);
      gctx.stroke();

      // 3. Mid curvature specular highlight
      gctx.strokeStyle = 'rgba(255, 255, 255, 0.055)';
      gctx.lineWidth = curveRadius * 0.7;
      gctx.beginPath();
      gctx.moveTo(xStart, yStart);
      gctx.quadraticCurveTo(cpX, cpY, xEnd, yEnd);
      gctx.stroke();

      // 4. Soft apex catch-light
      gctx.strokeStyle = 'rgba(255, 255, 255, 0.085)';
      gctx.lineWidth = 2.8 * dpr;
      gctx.beginPath();
      gctx.moveTo(xStart, yStart);
      gctx.quadraticCurveTo(cpX, cpY, xEnd, yEnd);
      gctx.stroke();

      // Chromatic dispersion
      const nx = isL ? 6.0 * dpr : -6.0 * dpr;
      gctx.strokeStyle = isL ? 'rgba(56, 189, 248, 0.015)' : 'rgba(251, 113, 133, 0.012)';
      gctx.lineWidth = curveRadius * 1.2;
      gctx.beginPath();
      gctx.moveTo(xStart - nx, yStart);
      gctx.quadraticCurveTo(cpX - nx, cpY, xEnd - nx, yEnd);
      gctx.stroke();

      gctx.strokeStyle = isL ? 'rgba(251, 113, 133, 0.012)' : 'rgba(56, 189, 248, 0.015)';
      gctx.lineWidth = curveRadius * 1.2;
      gctx.beginPath();
      gctx.moveTo(xStart + nx, yStart);
      gctx.quadraticCurveTo(cpX + nx, cpY, xEnd + nx, yEnd);
      gctx.stroke();
      gctx.restore();
    }

    // Top observation window border bevel
    const edgeBevel = gctx.createLinearGradient(0, 0, 0, 24 * dpr);
    edgeBevel.addColorStop(0, 'rgba(203, 213, 225, 0.16)');
    edgeBevel.addColorStop(0.3, 'rgba(56, 189, 248, 0.08)');
    edgeBevel.addColorStop(1, 'rgba(0, 0, 0, 0)');
    gctx.fillStyle = edgeBevel;
    gctx.fillRect(0, 0, width, 24 * dpr);

    // Subtle vignetted canopy corners + Baked Cinematic Edge Vignette (merged from renderColorGrade)
    const cx = width * 0.5, cy = height * 0.5;
    const maxR = Math.hypot(cx, cy);
    const baseVignette = gctx.createRadialGradient(cx, cy, maxR * 0.60, cx, cy, maxR * 0.98);
    baseVignette.addColorStop(0, 'rgba(0, 0, 0, 0)');
    baseVignette.addColorStop(0.55, 'rgba(0, 0, 0, 0)');
    baseVignette.addColorStop(0.85, 'rgba(3, 6, 14, 0.08)');
    baseVignette.addColorStop(1, 'rgba(1, 2, 8, 0.18)');
    gctx.fillStyle = baseVignette;
    gctx.fillRect(0, 0, width, height);

    const cornerGrad = gctx.createRadialGradient(cx, cy, Math.max(width, height) * 0.48, cx, cy, width * 0.85);
    cornerGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
    cornerGrad.addColorStop(0.8, 'rgba(14, 165, 233, 0.015)');
    cornerGrad.addColorStop(1, 'rgba(2, 6, 23, 0.08)');
    gctx.fillStyle = cornerGrad;
    gctx.fillRect(0, 0, width, height);
  },

  // 🪟 Observation Cockpit Glass Canopy Emulation (Screen-Space Viewport Pane)
  // Simulates observing the arena from behind a command observation deck glass window.
  // Map-agnostic (identical on every map): pre-baked static 3-pane faceted
  // canopy with prismatic refraction seams, dual diagonal glass reflection
  // sheen, and sunny celestial glare & anamorphic ray. Rain droplets live in
  // renderObservationGlassRain below since those only fit the human map.
  renderObservationGlassCanopy(ctx, battlefield) {
    if (this.quality === 'low') return;

    const width = this.canvas.width;
    const height = this.canvas.height;
    const dpr = (this.viewport && this.viewport.dpr) ? this.viewport.dpr : (window.devicePixelRatio || 1);
    const weather = this.getWeatherState();
    const { t, rainIntensity } = weather;

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // 1. Fast GPU Blit of pre-baked static canopy glass structure & bevels
    this.ensureStaticGlassCanopy(width, height, dpr);
    ctx.drawImage(this._staticGlassCanvas, 0, 0);

    // 2. Optical Transmission Blur along glass corridor (Ultra desktop tier only)
    const enableGlassDownsample = !this.isMobile && this.quality === 'ultra';
    if (enableGlassDownsample) {
      const dw = width, dh = height;
      const targetW = Math.min(240, Math.max(1, Math.round(dw / 8)));
      const targetH = Math.min(135, Math.max(1, Math.round(dh / 8)));
      if (!this._glassDownCanvas || this._glassDownW !== dw || this._glassDownH !== dh) {
        this._glassDownW = dw;
        this._glassDownH = dh;
        this._glassDownCanvas = document.createElement('canvas');
        this._glassDownCanvas.width = targetW;
        this._glassDownCanvas.height = targetH;
        this._glassDownCtx = this._glassDownCanvas.getContext('2d');
      }
      const gdcw = this._glassDownCanvas.width;
      const gdch = this._glassDownCanvas.height;
      this._glassDownCtx.clearRect(0, 0, gdcw, gdch);
      // Desktop-ultra Readback Consolidation (v3 §3 P1c #1):
      // Downsample from the already scaled _bloomCanvas instead of resolving full Retina framebuffer
      const src = this._bloomCanvas || this.canvas;
      this._glassDownCtx.drawImage(src, 0, 0, gdcw, gdch);

      const seamOffset = Math.round(32 * dpr);
      const curveRadius = Math.round(24 * dpr);
      const extY = Math.round(100 * dpr);
      const yStart = -extY;
      const yEnd = height + extY;

      const seams = [
        {
          xStart: Math.round((-extY / height) * seamOffset),
          xEnd: Math.round(seamOffset + (extY / height) * seamOffset),
          isL: true
        },
        {
          xStart: Math.round(width - (-extY / height) * seamOffset),
          xEnd: Math.round(width - seamOffset - (extY / height) * seamOffset),
          isL: false
        }
      ];

      for (const seam of seams) {
        const { xStart, xEnd, isL } = seam;
        const cpX = (xStart + xEnd) * 0.5 + (isL ? -2 * dpr : 2 * dpr);
        const cpY = height * 0.5;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(xStart + (isL ? -curveRadius * 0.8 : -curveRadius * 1.5), yStart);
        ctx.quadraticCurveTo(cpX + (isL ? -curveRadius * 0.75 : -curveRadius * 0.75), cpY, xEnd + (isL ? -curveRadius : -curveRadius * 0.5), yEnd);
        ctx.lineTo(xEnd + (isL ? curveRadius * 0.5 : curveRadius), yEnd);
        ctx.quadraticCurveTo(cpX + (isL ? curveRadius * 0.75 : curveRadius * 0.75), cpY, xStart + (isL ? curveRadius * 1.5 : curveRadius * 0.8), yStart);
        ctx.closePath();
        ctx.clip();

        ctx.globalAlpha = 0.35;
        ctx.drawImage(this._glassDownCanvas, 0, 0, gdcw, gdch, 0, 0, width, height);
        ctx.restore();
      }
    }

    // 3. Dual Diagonal Glass Reflection Sheen Bands (Screen blend mode, desktop high/ultra only)
    if (!this.isMobile && (this.quality === 'ultra' || this.quality === 'high')) {
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const sheenDrift = Math.sin(t * 0.22) * (width * 0.12);
      if (!this._sheenGrad || this._sheenW !== width || this._sheenH !== height) {
        this._sheenW = width;
        this._sheenH = height;
        const sheenGrad = ctx.createLinearGradient(0, 0, width, height);
        sheenGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
        sheenGrad.addColorStop(0.24, 'rgba(255, 255, 255, 0)');
        sheenGrad.addColorStop(0.28, 'rgba(56, 189, 248, 0.06)');
        sheenGrad.addColorStop(0.31, 'rgba(224, 242, 254, 0.14)');
        sheenGrad.addColorStop(0.34, 'rgba(56, 189, 248, 0.05)');
        sheenGrad.addColorStop(0.37, 'rgba(255, 255, 255, 0)');
        sheenGrad.addColorStop(0.40, 'rgba(255, 255, 255, 0.03)');
        sheenGrad.addColorStop(0.42, 'rgba(255, 255, 255, 0.10)');
        sheenGrad.addColorStop(0.44, 'rgba(255, 255, 255, 0)');
        sheenGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        this._sheenGrad = sheenGrad;
      }
      ctx.fillStyle = this._sheenGrad;
      ctx.fillRect(sheenDrift - width * 0.06, 0, width * 1.12, height);
      ctx.restore();
    }

    // ─────────────────────────────────────────────────────────────
    // 3. DRY / SUNNY WEATHER: CELESTIAL SUN GLARE & ANAMORPHIC FLARE (Desktop only)
    // ─────────────────────────────────────────────────────────────
    const dryFactor = Math.max(0, 1.0 - rainIntensity * 1.4);
    if (!this.isMobile && dryFactor > 0.05) {
      const glarePulse = 0.65 + 0.35 * Math.sin(t * 0.9);
      const sunX = width * 0.78 + Math.sin(t * 0.25) * 25 * dpr;
      const sunY = height * 0.13;

      ctx.save();
      ctx.globalCompositeOperation = 'screen';

      // 1. Radiant Golden-Amber Celestial Glare Bloom on the Glass (Cached Gradient)
      if (!this._sunBloomGrad || this._sunBloomH !== height || this._sunBloomDpr !== dpr) {
        this._sunBloomH = height;
        this._sunBloomDpr = dpr;
        const sGrad = ctx.createRadialGradient(0, 0, 8 * dpr, 0, 0, height * 0.7);
        sGrad.addColorStop(0, 'rgba(254, 240, 138, 0.25)');
        sGrad.addColorStop(0.25, 'rgba(251, 191, 36, 0.13)');
        sGrad.addColorStop(0.55, 'rgba(56, 189, 248, 0.05)');
        sGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        this._sunBloomGrad = sGrad;
      }
      ctx.save();
      ctx.translate(sunX, sunY);
      ctx.globalAlpha = glarePulse * dryFactor;
      ctx.fillStyle = this._sunBloomGrad;
      const sRadius = height * 0.7;
      ctx.fillRect(-sRadius, -sRadius, sRadius * 2, sRadius * 2);
      ctx.restore();

      // 2. (Removed) Full-width anamorphic horizontal flare ray - read as a
      // stray fixed horizontal line near the top of every map.

      // 3. Subtle Glass Lens Reflection Disks
      const flareDistX = width * 0.5 - sunX;
      const flareDistY = height * 0.5 - sunY;
      const fx1 = sunX + flareDistX * 0.45;
      const fy1 = sunY + flareDistY * 0.45;
      ctx.fillStyle = `rgba(56, 189, 248, ${0.08 * glarePulse * dryFactor})`;
      ctx.beginPath();
      ctx.arc(fx1, fy1, 24 * dpr, 0, Math.PI * 2);
      ctx.fill();

      // 4. Subtle Micro Glass Dust Motes
      const moteCount = this.isMobile ? 8 : 18;
      for (let m = 0; m < moteCount; m++) {
        const mx = ((m * 179 + 37) % (width - 40 * dpr)) + 20 * dpr;
        const my = ((m * 139 + 23) % (height - 30 * dpr)) + 15 * dpr;
        const moteGlint = Math.sin(t * 1.8 + m * 1.7);
        if (moteGlint > 0.2) {
          const mAlpha = (moteGlint - 0.2) * 0.45 * dryFactor;
          ctx.fillStyle = `rgba(255, 255, 255, ${mAlpha})`;
          ctx.fillRect(mx, my, 1.8 * dpr, 1.8 * dpr);
        }
      }
      ctx.restore();
    }

    ctx.restore();
  },

  // 🌧️ Rain Weather: Subtle Translucent 3D Glass Raindrops & Trickles
  // Split out of renderObservationGlassCanopy above since this part is tied
  // to the human map's own rain cycle (getWeatherState()) and has no
  // map-agnostic meaning - see the call site in renderPostProcess().
  renderObservationGlassRain(ctx, battlefield) {
    if (this.quality === 'low') return;

    const width = this.canvas.width;
    const height = this.canvas.height;
    const dpr = (this.viewport && this.viewport.dpr) ? this.viewport.dpr : (window.devicePixelRatio || 1);
    const weather = this.getWeatherState();
    const { t, rainIntensity } = weather;
    if (rainIntensity <= 0.01) return;

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    {
      const dropCount = this.isMobile ? 8 : 18; // lean on mobile, full detail on desktop
      for (let d = 0; d < dropCount; d++) {
        const seed = d * 7919 + 47;
        const seedX = (seed % 1000) / 1000;
        const seedY = ((seed * 17) % 1000) / 1000;
        const isFast = (d === 2 || d === 8 || d === 14); // 3 fast droplets that zip down
        const speed = isFast ? (34 + (d % 3) * 12) : (7 + (d % 4) * 3);
        const isTrickle = isFast || (d % 4 === 0);

        const startX = seedX * width;
        const slideProg = (t * (speed * (isFast ? 0.022 : 0.009)) + seedY * 4.0) % 4.0;

        if (slideProg < 3.4) {
          const dropLife = Math.min(1.0, Math.min(slideProg / (isFast ? 0.2 : 0.35), (3.4 - slideProg) / (isFast ? 0.3 : 0.45)));
          const dropAlpha = dropLife * Math.min(1.0, rainIntensity) * (isFast ? 0.65 : 0.55); // Translucent & subtle
          const wobble = Math.sin(t * (isFast ? 3.8 : 1.2) + d * 1.9) * (isFast ? 1.8 : (isTrickle ? 2.2 : 0.7)) * dpr;
          const curX = startX + wobble;
          const curY = (seedY * height * 0.5) + slideProg * (isFast ? 220 : (isTrickle ? 85 : 25)) * dpr;

          if (curY >= 0 && curY <= height) {
            const dropR = ((isFast ? 4.6 : (isTrickle ? 4.2 : 3.0)) + (d % 3) * 0.9) * dpr;

            ctx.save();
            ctx.globalAlpha = dropAlpha;

            // 1. Wet trailing smear behind sliding trickle (Paradigma 8: LUT)
            if (isTrickle && slideProg > (isFast ? 0.2 : 0.35)) {
              const trailLen = Math.min((isFast ? 75 : 45) * dpr, slideProg * (isFast ? 38 : 16) * dpr);
              const smearLUT = getTrailSmearLUT();
              if (smearLUT) {
                ctx.drawImage(smearLUT, curX - (isFast ? 0.9 : 0.75) * dpr, curY - trailLen, (isFast ? 1.8 : 1.5) * dpr, trailLen);
              }

              // Micro beads along the trail
              const beadCount = isFast ? 3 : 2;
              ctx.fillStyle = 'rgba(255, 255, 255, 0.50)';
              for (let b = 1; b <= beadCount; b++) {
                const by = curY - trailLen * (b * (isFast ? 0.26 : 0.38));
                const bx = curX - wobble * (b * 0.15);
                ctx.fillRect(bx - 0.5 * dpr, by - 0.5 * dpr, 1.0 * dpr, 1.0 * dpr);
              }
            }

            // 2. Refractive Glass Droplet Shadow (Soft lower outer rim)
            ctx.fillStyle = 'rgba(2, 6, 23, 0.45)';
            ctx.beginPath();
            ctx.ellipse(curX + 0.8 * dpr, curY + 1.0 * dpr, dropR * 1.15, dropR * 0.95, 0, 0, Math.PI * 2);
            ctx.fill();

            // 3. Translucent Water Droplet Body (Refractive cyan-white caustic lens via Paradigma 8 LUT)
            const dropLUT = getWaterDropLUT();
            if (dropLUT) {
              ctx.drawImage(dropLUT, curX - dropR, curY - dropR, dropR * 2, dropR * 2);
            }

            // 4. Soft White Specular Highlight Glint (Top-Left)
            ctx.fillStyle = 'rgba(255, 255, 255, 0.78)';
            ctx.beginPath();
            ctx.arc(curX - dropR * 0.35, curY - dropR * 0.35, dropR * 0.28, 0, Math.PI * 2);
            ctx.fill();

            // 5. Secondary Lower Caustics Light Reflection (Bottom-Right)
            ctx.fillStyle = 'rgba(255, 255, 255, 0.50)';
            ctx.beginPath();
            ctx.arc(curX + dropR * 0.30, curY + dropR * 0.32, dropR * 0.20, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();
          }
        }
      }

      // Subtle Glass Impact Splash Rings
      for (let s = 0; s < 2; s++) {
        const sSeed = s * 3491 + 13;
        const sTime = (t * 0.8 + (sSeed % 100) * 0.1) % 1.0;
        const sX = (((sSeed * 97) % 1000) / 1000) * width;
        const sY = (((sSeed * 43) % 1000) / 1000) * height * 0.85;
        const sRadius = sTime * 14 * dpr;
        const sAlpha = (1.0 - sTime) * 0.35 * rainIntensity;

        ctx.save();
        ctx.globalAlpha = sAlpha;
        ctx.strokeStyle = 'rgba(224, 242, 254, 1.0)';
        ctx.lineWidth = 1.0 * dpr;
        ctx.beginPath();
        ctx.arc(sX, sY, sRadius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }

    ctx.restore();
  }
};
